import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Alert,
  Textarea,
  Select,
  Code,
  TextInput,
  Badge,
  Progress,
  Loader,
  Divider
} from '@mantine/core';
import {
  IconArrowLeft,
  IconInfoCircle,
  IconPhoneCall,
  IconChecks,
  IconAlertCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import YAML from 'yaml';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';
import { listAllowlist } from '../api_scripts/allowlistClient';
import { startRun } from '../api_scripts/runsClient';
import { runBatchSequentially, reorderForPreconditionGrouping } from '../utils/batchRunner';
import { RuntimePreconditionGateModal, hasPreconditions } from './PreconditionsBadge';
import { EXAMPLE_PLAN_YAML } from '../utils/planSchema';
import { useDids } from '../hooks/useDids';

// fromDid is now sourced from /dids via useDids() — the SPA fetches the
// env-specific allowed list and seeds the Select with the primary. Hardcoded
// fallback kept only as a last-resort placeholder when the dids endpoint is
// unreachable.
const FALLBACK_FROM_DID_PLACEHOLDER = '+10000000000';

function RunTrigger({ onBack }) {
  const location = useLocation();
  // DiagramImporter / VerifyPlansScreen / future callers can preseed by
  // navigating with state: { prefillYaml, prefillDiagrams?, batchPlans? }.
  // Clear history.state on first read so a hard refresh doesn't keep
  // re-hydrating stale plans.
  const prefillYaml = location.state?.prefillYaml;
  const prefillDiagrams = location.state?.prefillDiagrams;
  const incomingBatch = location.state?.batchPlans;
  // When VerifyPlansScreen routes a multi-plan precondition-bearing queue
  // through here, it creates the in-browser batch row first and threads the
  // id so the runner can POST progress updates back. Null for single-plan
  // navigations and for batches without preconditions (which use the
  // durable server-side path).
  const inBrowserBatchId = location.state?.inBrowserBatchId ?? null;
  // One-shot init: when the caller hands us a multi-plan batch we reorder
  // by precondition signature here so the queue panel and the dial order
  // line up. Subsequent in-component edits to the YAML head don't re-reorder.
  const [initialQueue] = useState(() => {
    if (!prefillYaml || !Array.isArray(incomingBatch) || incomingBatch.length === 0) {
      return { headYaml: prefillYaml || EXAMPLE_PLAN_YAML, tail: null, reordered: false };
    }
    try {
      const head = YAML.parse(prefillYaml);
      const grouped = reorderForPreconditionGrouping([head, ...incomingBatch]);
      const newOrder = grouped.map((p) => p?.metadata?.id);
      const oldOrder = [head, ...incomingBatch].map((p) => p?.metadata?.id);
      const reordered = newOrder.some((id, i) => id !== oldOrder[i]);
      return {
        headYaml: YAML.stringify(grouped[0]),
        tail: grouped.slice(1),
        reordered
      };
    } catch {
      return { headYaml: prefillYaml, tail: incomingBatch, reordered: false };
    }
  });
  const [yaml, setYaml] = useState(initialQueue.headYaml);
  // Diagram refs flow through to the runner so the reporter can embed them in
  // the PDF. The user can't edit them; they're attached at the moment the
  // importer hands the plan off.
  const [diagrams] = useState(prefillDiagrams || []);
  // Additional plans queued after the prefillYaml plan. When set, RunTrigger
  // switches to batch mode: the YAML editor still shows plan #1, but a queue
  // panel lists the rest and "Start batch" replaces "Start run".
  const [batchPlans, setBatchPlans] = useState(initialQueue.tail);
  const { dids, primary: primaryDid, loading: didsLoading } = useDids();
  const [destinations, setDestinations] = useState([]);
  const [selectedDest, setSelectedDest] = useState(null);
  const [fromDid, setFromDid] = useState('');
  useEffect(() => {
    if (!fromDid && primaryDid) setFromDid(primaryDid);
  }, [primaryDid, fromDid]);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);
  const navigate = useNavigate();
  const backendConfigured = isSitBackendConfigured();
  const resultsRef = useRef(null);

  // Same scroll-to-result pattern as the importers / Agent Studio so the
  // user lands on the freshly-started run info instead of staring at the
  // YAML editor they just submitted.
  useEffect(() => {
    if (lastResult && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [lastResult]);

  useEffect(() => {
    if (prefillYaml) {
      window.history.replaceState({}, '');
      const queued = Array.isArray(incomingBatch) ? incomingBatch.length : 0;
      notifications.show({
        title: queued > 0 ? `${queued + 1} plans queued` : 'Plan prefilled',
        message:
          queued > 0
            ? 'Pick a destination — runs will execute sequentially.'
            : 'Imported plan loaded — pick a destination and start the run.',
        color: 'violet',
        autoClose: 4000
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllowlist = useCallback(async () => {
    if (!backendConfigured) return;
    try {
      const entries = await listAllowlist();
      setDestinations(entries.map((e) => ({ value: e.did, label: `${e.did} — ${e.owner}` })));
    } catch (err) {
      notifications.show({
        title: 'Could not load allowlist',
        message: err?.response?.data?.message ?? err.message,
        color: 'red'
      });
    }
  }, [backendConfigured]);

  useEffect(() => {
    loadAllowlist();
  }, [loadAllowlist]);

  const parsePlanFromYaml = () => {
    let parsed;
    try {
      parsed = YAML.parse(yaml);
    } catch (err) {
      const msg = err?.message ?? 'plan parse failed';
      setParseError(msg);
      notifications.show({
        title: 'Plan parse failed',
        message: msg,
        color: 'red',
        autoClose: 8000
      });
      return null;
    }
    if (!parsed || typeof parsed !== 'object') {
      setParseError('Plan parsed to a non-object value.');
      return null;
    }
    return parsed;
  };

  // Runtime gate state — opened by the batchRunner's onPreconditionChange
  // hook (or, for a single run, by handleStart) and closed when the engineer
  // clicks Resume / Skip / Cancel.
  const [gate, setGate] = useState(null); // { plan, position, resolve }

  const openGate = (plan, position) =>
    new Promise((resolve) => setGate({ plan, position, resolve }));
  const resolveGate = (decision) => {
    const r = gate?.resolve;
    setGate(null);
    r?.(decision);
  };

  const handleStart = async () => {
    setParseError(null);
    const parsed = parsePlanFromYaml();
    if (!parsed) return;
    parsed.target = parsed.target ?? {};
    if (selectedDest) parsed.target.destination = selectedDest;
    if (fromDid) parsed.target.fromDid = fromDid;

    if (hasPreconditions(parsed)) {
      const decision = await openGate(parsed, { index: 0, total: 1 });
      if (!decision?.proceed) return;
    }
    return fireStart(parsed);
  };

  const fireStart = async (parsed) => {
    setSubmitting(true);
    try {
      const result = await startRun(parsed, {
        idempotencyKey: crypto.randomUUID(),
        ...(diagrams.length > 0 ? { diagrams } : {})
      });
      setLastResult(result);
      notifications.show({
        title: 'Run started',
        message: `testRunId: ${result.testRunId}`,
        color: 'green'
      });
    } catch (err) {
      const body = err?.response?.data;
      const ctxErrors = body?.context?.errors;
      const msg = Array.isArray(ctxErrors)
        ? ctxErrors.map((e) => `${e.path}: ${e.message}`).join('; ')
        : (body?.message ?? err.message);
      notifications.show({
        title: 'Run failed',
        message: msg,
        color: 'red',
        autoClose: 10000
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartBatch = async () => {
    setParseError(null);
    const head = parsePlanFromYaml();
    if (!head) return;
    // Dial in display order — the queue panel already reflects the
    // precondition-grouped order from one-shot reorder at hydration.
    const queue = [head, ...(batchPlans || [])];
    return fireBatch(queue);
  };

  const fireBatch = async (queue) => {
    setSubmitting(true);
    setBatchProgress({ total: queue.length, done: 0, current: null, results: [], errors: [] });
    try {
      const final = await runBatchSequentially({
        plans: queue,
        overrides: {
          ...(selectedDest ? { destination: selectedDest } : {}),
          ...(fromDid ? { fromDid } : {})
        },
        diagrams,
        onProgress: setBatchProgress,
        onPreconditionChange: (plan, position) => openGate(plan, position),
        ...(inBrowserBatchId ? { batchId: inBrowserBatchId } : {})
      });
      notifications.show({
        title: final?.cancelled ? 'Batch cancelled' : 'Batch complete',
        message: `${queue.length} planned, ${final?.cancelled ? 'stopped early' : 'all runs finished'}`,
        color: final?.cancelled ? 'yellow' : 'green'
      });
    } catch (err) {
      notifications.show({
        title: 'Batch failed',
        message: err?.message || 'unknown error',
        color: 'red'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inBatchMode = Array.isArray(batchPlans) && batchPlans.length > 0;
  const batchSize = (inBatchMode ? batchPlans.length : 0) + 1;
  const planTitle = (plan, i) =>
    plan?.metadata?.name || plan?.metadata?.id || `Plan ${i + 1}`;
  const queuedTitles = useMemo(
    () => (batchPlans || []).map((p, i) => planTitle(p, i + 1)),
    [batchPlans]
  );

  return (
    <Container size="md" py="lg">
      <Group mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to SIT Home
        </Button>
      </Group>

      <Stack gap="md">
        <Group gap="sm">
          <Title order={2}>{inBatchMode ? 'Run a Batch of Tests' : 'Run a Test Call'}</Title>
          {inBatchMode && (
            <Badge color="violet" variant="light">{batchSize} plans queued</Badge>
          )}
        </Group>

        {inBatchMode && (
          <Card withBorder padding="md" radius="md">
            <Group gap="xs" mb="xs">
              <IconChecks size={16} />
              <Text fw={600} size="sm">Batch queue</Text>
              <Text size="xs" c="dimmed">Runs execute one at a time.</Text>
              {initialQueue.reordered && (
                <Badge size="xs" color="orange" variant="light">Auto-grouped by IVR setup</Badge>
              )}
            </Group>
            <Stack gap={4}>
              <Group gap="xs">
                <Badge size="xs" variant="light" color="violet">1</Badge>
                <Text size="sm" fw={500}>(YAML editor below)</Text>
                <Text size="xs" c="dimmed">— edit before starting</Text>
              </Group>
              {queuedTitles.map((name, i) => (
                <Group key={i} gap="xs">
                  <Badge size="xs" variant="light" color="gray">{i + 2}</Badge>
                  <Text size="sm">{name}</Text>
                </Group>
              ))}
            </Stack>
            <Group justify="flex-end" mt="sm">
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => setBatchPlans(null)}
              >
                Discard queue (single run)
              </Button>
            </Group>
          </Card>
        )}

        {!backendConfigured && (
          <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
            The SIT backend URL is not configured. Set <code>REACT_APP_SIT_API_BASE</code> in your
            <code> .env</code> to enable real runs. You can still draft plans in the editor.
          </Alert>
        )}

        <Card withBorder padding="lg" radius="md">
          <Stack gap="md">
            <Group grow align="flex-start">
              <Select
                label="Destination (allowlist)"
                placeholder="Pick an allowlisted DID"
                data={destinations}
                value={selectedDest}
                onChange={setSelectedDest}
                disabled={!backendConfigured || destinations.length === 0}
                searchable
                description={
                  destinations.length === 0 && backendConfigured
                    ? 'Allowlist is empty — add a DID under "Destination Allowlist" first.'
                    : 'Overrides target.destination in the plan.'
                }
              />
              <Select
                label="From DID (harness)"
                placeholder={didsLoading ? 'Loading…' : (dids.length === 0 ? 'No DIDs configured in this env' : 'Pick a Chime DID')}
                data={dids.map((d) => ({ value: d.did, label: d.primary ? `${d.did} (primary)` : d.did }))}
                value={fromDid || (didsLoading ? '' : FALLBACK_FROM_DID_PLACEHOLDER)}
                onChange={(v) => setFromDid(v || '')}
                searchable
                disabled={!backendConfigured || didsLoading || dids.length === 0}
                description="Overrides target.fromDid in the plan."
              />
            </Group>
            <Textarea
              label="Test plan (YAML or JSON)"
              description="Imported plans from the Diagram Importer can be pasted here directly (YAML or JSON)."
              minRows={16}
              autosize
              value={yaml}
              onChange={(e) => setYaml(e.currentTarget.value)}
              error={parseError}
              styles={{ input: { fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 } }}
            />
            <Group justify="flex-end">
              <Button
                color="violet"
                leftSection={inBatchMode ? <IconChecks size={16} /> : <IconPhoneCall size={16} />}
                onClick={inBatchMode ? handleStartBatch : handleStart}
                loading={submitting}
                disabled={!backendConfigured}
              >
                {inBatchMode ? `Start batch (${batchSize} runs)` : 'Start run'}
              </Button>
            </Group>
          </Stack>
        </Card>

        {batchProgress && (
          <Card withBorder padding="md" radius="md">
            <Group justify="space-between" mb="xs">
              <Text fw={600}>Batch progress</Text>
              <Badge color={batchProgress.done === batchProgress.total ? 'green' : 'violet'} variant="light">
                {batchProgress.done} / {batchProgress.total}
              </Badge>
            </Group>
            <Progress
              value={(batchProgress.done / batchProgress.total) * 100}
              animated={batchProgress.done < batchProgress.total}
              mb="sm"
            />
            {batchProgress.current && (
              <Group gap="xs" mb="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  <strong>{batchProgress.current.name}</strong> — {batchProgress.current.status}
                </Text>
              </Group>
            )}
            {batchProgress.results.length > 0 && (
              <>
                <Divider my="xs" />
                <Stack gap={4}>
                  {batchProgress.results.map((r) => (
                    <Group key={r.testRunId} gap="xs">
                      <Badge color={r.finalStatus === 'COMPLETED' ? 'green' : 'yellow'} variant="light" size="xs">
                        {r.finalStatus}
                      </Badge>
                      <Code>{r.testRunId}</Code>
                      <Text size="xs" c="dimmed">{r.name}</Text>
                      <Button size="xs" variant="subtle" onClick={() => navigate(`/history/${r.testRunId}`)}>
                        report
                      </Button>
                    </Group>
                  ))}
                </Stack>
              </>
            )}
            {batchProgress.errors.length > 0 && (
              <>
                <Divider my="xs" label="Errors" />
                <Stack gap={4}>
                  {batchProgress.errors.map((e, i) => (
                    <Group key={i} gap="xs">
                      <IconAlertCircle size={14} color="red" />
                      <Text size="xs" fw={500}>{e.name}</Text>
                      <Text size="xs" c="dimmed">{e.error}</Text>
                    </Group>
                  ))}
                </Stack>
              </>
            )}
          </Card>
        )}

        {lastResult && (
          <div ref={resultsRef} style={{ scrollMarginTop: 16 }}>
          <Card withBorder padding="lg" radius="md">
            <Stack gap="xs">
              <Text fw={600}>Run started</Text>
              <Text size="sm">
                <strong>testRunId:</strong> <Code>{lastResult.testRunId}</Code>
              </Text>
              <Text size="sm">
                <strong>correlationId:</strong> <Code>{lastResult.correlationId}</Code>
              </Text>
              <Text size="sm">
                <strong>executionArn:</strong> <Code>{lastResult.executionArn}</Code>
              </Text>
              <Group justify="flex-end">
                <Button
                  variant="light"
                  color="violet"
                  onClick={() => navigate(`/history/${lastResult.testRunId}`)}
                >
                  Open report
                </Button>
              </Group>
            </Stack>
          </Card>
          </div>
        )}
      </Stack>

      <RuntimePreconditionGateModal
        opened={!!gate}
        plan={gate?.plan}
        position={gate?.position}
        onResume={() => resolveGate({ proceed: true })}
        onSkip={() => resolveGate({ skip: true })}
        onCancel={() => resolveGate({ cancel: true })}
      />
    </Container>
  );
}

export default RunTrigger;
